/**
 * CrewManagementDialog — Manage crew members, assign to ships, recruit, and buy slots.
 *
 * Side-by-side layout: crew list (left) | detail panel (right).
 */

import Phaser from 'phaser';
import { ServiceLocator, Services } from '../../utils/ServiceLocator';
import type { MMOMonetizationManager } from '../../managers/MMOMonetizationManager';
import type { MMOStateManager } from '../../managers/MMOStateManager';
import type { PaymentFlowManager } from '../../managers/PaymentFlowManager';
import type { Crew, CrewSlot } from '../../shared/common/MonetizationTypes';
import { Colors, CSSColors, Fonts, FontSize, Radius, createDialogChrome, closeDialog } from './GameUITheme';
import { ShipStoreDialog } from './ShipStoreDialog';
import { I18n } from '../../i18n/I18nService';
import { isMobile } from '../../utils/ResponsiveHelper';

const PAD = 10;

const XP_PER_LEVEL = 1000;
const MAX_CREW_LEVEL = 10;

/** Credit cost to reach the next level. Incremental: 100 × current level. */
function getLevelUpCost(level: number, totalXp: number): number {
  const fullCost = 100 * level;
  const xpInLevel = totalXp % XP_PER_LEVEL;
  const xpRemaining = XP_PER_LEVEL - xpInLevel;
  return Math.ceil(fullCost * (xpRemaining / XP_PER_LEVEL));
}

// All bonuses capped at 10%
const MAX_BONUS = 10;

const BONUS_DEFS: { label: string; skillKey: keyof Crew }[] = [
  { label: 'Attack',  skillKey: 'combatSkill' },
  { label: 'Defense', skillKey: 'combatSkill' },
  { label: 'Speed',   skillKey: 'pilotingSkill' },
  { label: 'Evasion', skillKey: 'pilotingSkill' },
  { label: 'Sensor',  skillKey: 'scienceSkill' },
  { label: 'Repair',  skillKey: 'engineeringSkill' },
];

/** Calculate a bonus % to 2 decimal places (capped at MAX_BONUS) */
function calcBonus(crew: Crew, b: typeof BONUS_DEFS[number]): number {
  const skill = (crew[b.skillKey] as number) || 0;
  const raw = (skill / 100) * MAX_BONUS;
  return Math.min(MAX_BONUS, Math.round(raw * 100) / 100);
}

/** Color bands inspired by Football Manager attribute colors */
function bonusColor(val: number): string {
  if (val <= 0)    return '#8c3a3a';   // 0%        — dark red (terrible)
  if (val <= 2.5)  return '#cc5544';   // 0–2.5%    — red (poor)
  if (val <= 5)    return '#dd8833';   // 2.5–5%    — orange (below avg)
  if (val <= 7.5)  return '#ccbb33';   // 5–7.5%    — yellow (average)
  if (val < 9)     return '#66cc44';   // 7.5–9%    — green (good)
  if (val < 10)    return '#33dd55';   // 9–9.99%   — bright green (excellent)
  return '#22ee66';                    // 10% (max) — vivid green (perfect)
}

// Skills data
const SKILL_NAMES = ['Combat', 'Piloting', 'Navigation', 'Engineering', 'Science'];
const SKILL_KEYS: (keyof Crew)[] = ['combatSkill', 'pilotingSkill', 'navigationSkill', 'engineeringSkill', 'scienceSkill'];
const SKILL_HEX_COLORS = [0xff4444, 0x44aaff, 0x44ff88, 0xffaa44, 0xcc66ff];
const SKILL_CSS_COLORS = ['#ff4444', '#44aaff', '#44ff88', '#ffaa44', '#cc66ff'];

// Mini card bar labels
const SKILL_LABELS = ['C', 'P', 'N', 'E', 'S'];

/** State for ship picker sub-view */
type DetailView = 'info' | 'ship-picker' | 'dismiss-confirm';

export class CrewManagementDialog extends Phaser.GameObjects.Container {
  private monetization: MMOMonetizationManager;
  private mmoState: MMOStateManager;
  private crews: Crew[] = [];
  private slots: CrewSlot[] = [];
  private selectedCrew: Crew | null = null;
  private detailView: DetailView = 'info';

  private listContainer: Phaser.GameObjects.Container;
  private detailContainer: Phaser.GameObjects.Container;
  private statusText: Phaser.GameObjects.Text;

  // Responsive
  private mobile: boolean;
  private listW: number;
  private detailW: number;

  constructor(scene: Phaser.Scene) {
    const mobile = isMobile(scene);
    const dw = mobile ? 340 : 652;
    const dh = mobile ? 540 : 600;
    const { width, height } = scene.cameras.main;
    super(scene, width / 2 - dw / 2, height / 2 - dh / 2);
    this.mobile = mobile;

    // Responsive layout
    const LIST_W = mobile ? dw - PAD * 2 : 260;
    const DETAIL_X = LIST_W + PAD * 2 + 10;
    this.listW = LIST_W;
    this.detailW = mobile ? dw - PAD * 2 : dw - DETAIL_X - PAD;

    this.monetization = ServiceLocator.get<MMOMonetizationManager>(Services.MMO_MONETIZATION);
    this.mmoState = ServiceLocator.get<MMOStateManager>(Services.MMO_STATE);

    createDialogChrome(scene, this, dw, dh, {
      title: I18n.t('dialog.crew_quarters'),
      onClose: () => this.close(),
    });

    // Left: crew list
    this.listContainer = scene.add.container(PAD, 58);
    this.add(this.listContainer);

    // Detail overlay (hidden until item tapped on mobile)
    this.detailContainer = scene.add.container(mobile ? PAD : DETAIL_X, 58);
    if (mobile) this.detailContainer.setVisible(false);
    this.add(this.detailContainer);

    // Status text
    this.statusText = scene.add.text(dw / 2, dh - 15, '', {
      fontSize: FontSize.xs, color: '#667788', fontFamily: Fonts.body,
    });
    this.statusText.setOrigin(0.5);
    this.add(this.statusText);

    this.setDepth(2000);
    scene.add.existing(this);

    this.loadCrews();
  }

  private close(): void {
    closeDialog(this.scene, this);
  }

  // ============ Mobile Back Button ============

  private createMobileBackBtn(): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(0, 0);
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x1e2844, 0.9);
    bg.fillRoundedRect(0, 0, 80, 26, 4);
    bg.lineStyle(1, 0x00ffff, 0.1);
    bg.strokeRoundedRect(0, 0, 80, 26, 4);
    btn.add(bg);
    btn.add(this.scene.add.text(40, 13, '\u2190 Back', {
      fontSize: '12px', color: '#8899bb', fontFamily: 'Share Tech Mono, monospace',
    }).setOrigin(0.5));
    const zone = this.scene.add.zone(0, 0, 80, 26).setOrigin(0, 0);
    zone.setInteractive(new Phaser.Geom.Rectangle(0, 0, 80, 26), Phaser.Geom.Rectangle.Contains);
    zone.on('pointerdown', () => {
      this.listContainer.setVisible(true);
      this.detailContainer.setVisible(false);
    });
    btn.add(zone);
    return btn;
  }

  private async loadCrews(): Promise<void> {
    this.setStatus('Loading crew...');
    const data = await this.monetization.fetchCrewList();
    this.crews = data.crews.sort((a, b) => b.level - a.level || b.totalXp - a.totalXp);
    this.slots = data.slots;
    this.setStatus('');

    if (this.selectedCrew) {
      // Re-select from fresh data so stats are up to date
      this.selectedCrew = this.crews.find(c => c.id === this.selectedCrew!.id) || this.crews[0] || null;
    } else if (this.crews.length > 0) {
      this.selectedCrew = this.crews[0];
    }

    this.renderList();
    this.fetchAndRenderDetail();
  }

  /** Resolve a ship ID to a human-readable name via MMOStateManager */
  private getShipLabel(shipId: string | null): string {
    if (!shipId) return '';
    const unit = this.mmoState.getUnit(shipId);
    return unit ? unit.type : shipId.substring(0, 12) + '\u2026';
  }

  // ============ Crew List (left column) ============

  private renderList(): void {
    this.listContainer.removeAll(true);
    let y = 0;

    // Slots info
    const totalSlots = this.slots.length;
    const usedSlots = this.crews.length;
    const slotsText = this.scene.add.text(0, y, `Crew: ${usedSlots}/${totalSlots} slots`, {
      fontSize: FontSize.sm, color: '#8899bb', fontFamily: Fonts.body,
    });
    this.listContainer.add(slotsText);
    y += 22;

    // Action buttons row
    this.listContainer.add(this.createSmallBtn(0, y, 'Recruit', 80, 0x224422, () => this.handleRecruit()));
    this.listContainer.add(this.createSmallBtn(88, y, 'Buy Slot $10', 110, 0x224488, () => this.handleBuySlot()));
    y += 32;

    // Separator
    const sep = this.scene.add.graphics();
    sep.lineStyle(1, Colors.cyan, 0.1);
    sep.lineBetween(0, y, this.listW, y);
    this.listContainer.add(sep);
    y += 8;

    // Assigned crews
    const assignedCrews = this.crews.filter(c => c.status === 'assigned');
    if (assignedCrews.length > 0) {
      this.listContainer.add(this.scene.add.text(0, y, 'ASSIGNED', {
        fontSize: FontSize.xs, color: '#667788', fontFamily: Fonts.heading,
      }));
      y += 18;

      for (const crew of assignedCrews) {
        this.listContainer.add(this.createCrewCard(0, y, crew));
        y += 54;
      }
      y += 4;
    }

    // Barracks crews
    const barracksCrews = this.crews.filter(c => c.status === 'barracks');
    if (barracksCrews.length > 0) {
      this.listContainer.add(this.scene.add.text(0, y, 'BARRACKS', {
        fontSize: FontSize.xs, color: '#667788', fontFamily: Fonts.heading,
      }));
      y += 18;

      for (const crew of barracksCrews) {
        this.listContainer.add(this.createCrewCard(0, y, crew));
        y += 54;
      }
    }

    if (this.crews.length === 0) {
      const emptyText = this.scene.add.text(this.listW / 2, y + 30, 'No crew members yet.\nClick "Recruit" to hire one.', {
        fontSize: FontSize.sm, color: '#556677', fontFamily: Fonts.body, align: 'center',
      });
      emptyText.setOrigin(0.5, 0);
      this.listContainer.add(emptyText);
    }
  }

  private createCrewCard(x: number, y: number, crew: Crew): Phaser.GameObjects.Container {
    const card = this.scene.add.container(x, y);
    const cardH = 48;
    const isSelected = this.selectedCrew?.id === crew.id;

    const cardBg = this.scene.add.graphics();
    const drawBg = (selected: boolean, hover: boolean) => {
      cardBg.clear();
      const fill = selected ? 0x253a66 : hover ? 0x1e1e44 : 0x161630;
      cardBg.fillStyle(fill, selected ? 0.9 : 0.8);
      cardBg.fillRoundedRect(0, 0, this.listW, cardH, Radius.sm);
      const bColor = selected ? 0x44aaff : hover ? Colors.cyan : Colors.cyan;
      const bAlpha = selected ? 0.4 : hover ? 0.15 : 0.08;
      cardBg.lineStyle(1, bColor, bAlpha);
      cardBg.strokeRoundedRect(0, 0, this.listW, cardH, Radius.sm);
    };
    drawBg(isSelected, false);
    card.add(cardBg);

    // Status indicator dot
    const statusColor = crew.status === 'assigned' ? 0x44ff44 : crew.status === 'in_combat' ? 0xff4444 : 0x888888;
    card.add(this.scene.add.circle(14, cardH / 2, 4, statusColor));

    // Name + Level
    card.add(this.scene.add.text(26, 6, `${crew.name}  Lv.${crew.level}`, {
      fontSize: FontSize.sm, color: '#ccddff', fontFamily: Fonts.heading,
    }));

    // Status / ship assignment (resolved name)
    let statusLabel = crew.status === 'barracks' ? 'In barracks' : crew.status === 'assigned' ? 'Assigned' : crew.status;
    if (crew.assignedShipId) {
      statusLabel = `On ${this.getShipLabel(crew.assignedShipId)}`;
    }
    card.add(this.scene.add.text(26, 24, statusLabel, {
      fontSize: FontSize.xs, color: '#667788', fontFamily: Fonts.body,
    }));

    // Mini skill bars with labels (right side)
    const barStartX = this.listW - 70;
    const barWidth = 46;
    const barHeight = 4;
    const skills = SKILL_KEYS.map(k => (crew[k] as number) || 0);
    const colors = SKILL_HEX_COLORS;

    for (let i = 0; i < 5; i++) {
      const barY = 7 + i * 7;

      // Letter label
      const lbl = this.scene.add.text(barStartX - 10, barY - 1, SKILL_LABELS[i], {
        fontSize: '8px', color: '#556677', fontFamily: Fonts.body,
      });
      card.add(lbl);

      // Background
      const barBg = this.scene.add.rectangle(barStartX, barY, barWidth, barHeight, 0x222233);
      barBg.setOrigin(0, 0);
      card.add(barBg);

      // Fill
      const fillW = Math.max(1, (skills[i] / 100) * barWidth);
      const barFill = this.scene.add.rectangle(barStartX, barY, fillW, barHeight, colors[i]);
      barFill.setOrigin(0, 0);
      card.add(barFill);
    }

    // Interactive zone
    const hitZone = this.scene.add.zone(0, 0, this.listW, cardH).setOrigin(0, 0);
    hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.listW, cardH), Phaser.Geom.Rectangle.Contains);
    hitZone.on('pointerover', () => {
      if (!isSelected) drawBg(false, true);
      this.scene.input.setDefaultCursor('pointer');
    });
    hitZone.on('pointerout', () => {
      drawBg(isSelected, false);
      this.scene.input.setDefaultCursor('default');
    });
    hitZone.on('pointerdown', () => {
      this.selectedCrew = crew;
      this.detailView = 'info';
      this.renderList();
      this.fetchAndRenderDetail();
      if (this.mobile) {
        this.listContainer.setVisible(false);
        this.detailContainer.setVisible(true);
      }
    });
    card.add(hitZone);

    return card;
  }

  // ============ Detail Panel (right column) ============

  private fetchAndRenderDetail(): void {
    this.renderDetail();
  }

  private renderDetail(): void {
    this.detailContainer.removeAll(true);

    if (!this.selectedCrew) {
      const hint = this.scene.add.text(this.detailW / 2, 80, 'Select a crew member\nto view details', {
        fontSize: FontSize.sm, color: '#556677', fontFamily: Fonts.body, align: 'center',
      });
      hint.setOrigin(0.5, 0);
      this.detailContainer.add(hint);
      return;
    }

    switch (this.detailView) {
      case 'info': this.renderInfoView(); break;
      case 'ship-picker': this.renderShipPicker(); break;
      case 'dismiss-confirm': this.renderDismissConfirm(); break;
    }
  }

  private renderInfoView(): void {
    const crew = this.selectedCrew!;
    let y = 0;

    // Mobile back button
    if (this.mobile) {
      const backBtn = this.createMobileBackBtn();
      this.detailContainer.add(backBtn);
      y += 30;
    }

    // Name
    this.detailContainer.add(this.scene.add.text(0, y, crew.name, {
      fontSize: FontSize.xl, color: CSSColors.textWhite, fontFamily: Fonts.heading,
    }));
    y += 26;

    // Level + XP bar
    const isMaxLevel = crew.level >= MAX_CREW_LEVEL;
    const xpInLevel = crew.totalXp % XP_PER_LEVEL;
    const xpForNext = XP_PER_LEVEL;
    const xpPct = isMaxLevel ? 1 : xpInLevel / xpForNext;

    const levelLine = this.scene.add.text(0, y, `Level ${crew.level}${isMaxLevel ? ' (MAX)' : ` / ${MAX_CREW_LEVEL}`}`, {
      fontSize: FontSize.sm, color: isMaxLevel ? '#ffd700' : '#aaccff', fontFamily: Fonts.heading,
    });
    this.detailContainer.add(levelLine);

    const xpLabel = isMaxLevel ? 'MAX' : `${xpInLevel}/${xpForNext} XP`;
    const xpText = this.scene.add.text(this.detailW, y, xpLabel, {
      fontSize: FontSize.xs, color: isMaxLevel ? '#ffd700' : '#889999', fontFamily: Fonts.body,
    });
    xpText.setOrigin(1, 0);
    this.detailContainer.add(xpText);
    y += 18;

    // XP progress bar
    const xpBarW = this.detailW;
    const xpBarH = 6;
    const xpBarBg = this.scene.add.graphics();
    xpBarBg.fillStyle(0x222233, 1);
    xpBarBg.fillRoundedRect(0, y, xpBarW, xpBarH, 2);
    this.detailContainer.add(xpBarBg);

    const xpFillW = Math.max(2, xpPct * xpBarW);
    const xpBarFill = this.scene.add.graphics();
    xpBarFill.fillStyle(isMaxLevel ? 0xffd700 : 0x44aaff, 1);
    xpBarFill.fillRoundedRect(0, y, xpFillW, xpBarH, 2);
    this.detailContainer.add(xpBarFill);
    y += 14;

    // Assignment status
    if (crew.assignedShipId) {
      const shipLabel = this.getShipLabel(crew.assignedShipId);
      this.addInfoRow(0, y, 'Assigned to', shipLabel, CSSColors.green);
      y += 20;
    } else if (crew.status === 'assigned') {
      this.addInfoRow(0, y, 'Status', 'Assigned (ship unknown)', '#aaccdd');
      y += 20;
    } else {
      this.addInfoRow(0, y, 'Status', 'In barracks', '#888888');
      y += 20;
    }

    y += 2;
    this.addDivider(y); y += 8;

    // Skills section
    this.detailContainer.add(this.scene.add.text(0, y, 'SKILLS', {
      fontSize: FontSize.xs, color: '#5588aa', fontFamily: Fonts.heading,
    }));
    y += 16;

    for (let i = 0; i < SKILL_NAMES.length; i++) {
      const value = (crew[SKILL_KEYS[i]] as number) || 0;

      const label = this.scene.add.text(0, y, SKILL_NAMES[i], {
        fontSize: FontSize.xs, color: '#aabbcc', fontFamily: Fonts.body,
      });
      this.detailContainer.add(label);

      const valColor = value >= 100 ? '#ffd700' : SKILL_CSS_COLORS[i];
      const valText = this.scene.add.text(this.detailW, y, `${value}/100`, {
        fontSize: FontSize.xs, color: valColor, fontFamily: Fonts.heading,
      });
      valText.setOrigin(1, 0);
      this.detailContainer.add(valText);
      y += 14;

      const barW = this.detailW;
      const barH = 5;
      const barBg = this.scene.add.rectangle(0, y, barW, barH, 0x222233).setOrigin(0, 0);
      this.detailContainer.add(barBg);
      const fillW = Math.max(1, (value / 100) * barW);
      const barFill = this.scene.add.rectangle(0, y, fillW, barH, SKILL_HEX_COLORS[i]).setOrigin(0, 0);
      this.detailContainer.add(barFill);
      y += 12;
    }

    y += 4;
    this.addDivider(y); y += 8;

    // Ship Bonuses section — calculated directly from skills
    this.detailContainer.add(this.scene.add.text(0, y, 'SHIP BONUSES', {
      fontSize: FontSize.xs, color: '#5588aa', fontFamily: Fonts.heading,
    }));
    y += 16;

    const ROW_H = 22;
    const ROW_PAD = 4;

    for (const b of BONUS_DEFS) {
      const val = calcBonus(crew, b);
      const sign = val > 0 ? '+' : '';
      const valStr = val === 0 ? '+0.00%' : `${sign}${val.toFixed(2)}%`;

      // Row container for hover highlight
      const row = this.scene.add.container(0, y);

      // Hover background (hidden by default)
      const hoverBg = this.scene.add.graphics();
      row.add(hoverBg);

      // Label (left)
      const label = this.scene.add.text(ROW_PAD, 3, b.label, {
        fontSize: FontSize.xs, color: '#aabbcc', fontFamily: Fonts.body,
      });
      row.add(label);

      // Value (right)
      const valText = this.scene.add.text(this.detailW - ROW_PAD, 3, valStr, {
        fontSize: FontSize.xs, color: bonusColor(val), fontFamily: Fonts.heading,
      });
      valText.setOrigin(1, 0);
      row.add(valText);

      // Dotted leader line between label and value
      const dotsG = this.scene.add.graphics();
      const dotsY = 11;
      const dotStart = label.x + label.width + 6;
      const dotEnd = this.detailW - ROW_PAD - valText.width - 6;
      dotsG.fillStyle(0x445566, 0.4);
      for (let dx = dotStart; dx < dotEnd; dx += 5) {
        dotsG.fillCircle(dx, dotsY, 1);
      }
      row.add(dotsG);

      // Interactive hit zone with hover highlight
      const hitZone = this.scene.add.zone(0, 0, this.detailW, ROW_H).setOrigin(0, 0);
      hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.detailW, ROW_H), Phaser.Geom.Rectangle.Contains);
      hitZone.on('pointerover', () => {
        hoverBg.clear();
        hoverBg.fillStyle(0x44aaff, 0.08);
        hoverBg.fillRoundedRect(0, 0, this.detailW, ROW_H, 3);
        label.setColor('#ffffff');
      });
      hitZone.on('pointerout', () => {
        hoverBg.clear();
        label.setColor('#aabbcc');
      });
      row.add(hitZone);

      this.detailContainer.add(row);
      y += ROW_H;
    }

    y += 10;

    // Action buttons
    if (crew.status === 'barracks') {
      this.detailContainer.add(this.createActionBtn(0, y, this.detailW, 32, 'Assign to Ship', 0x224422, () => {
        this.detailView = 'ship-picker';
        this.renderDetail();
      }));
      y += 40;
    } else if (crew.status === 'assigned') {
      this.detailContainer.add(this.createActionBtn(0, y, this.detailW, 32, 'Unassign', 0x443322, () => this.handleUnassign(crew)));
      y += 40;
    }

    // Level Up button (disabled at max level)
    if (isMaxLevel) {
      // Show a muted "Max Level Reached" indicator
      const maxLvlBg = this.scene.add.graphics();
      maxLvlBg.fillStyle(0x222222, 0.5);
      maxLvlBg.fillRoundedRect(0, y, this.detailW, 32, Radius.sm);
      maxLvlBg.lineStyle(1, 0xffd700, 0.15);
      maxLvlBg.strokeRoundedRect(0, y, this.detailW, 32, Radius.sm);
      this.detailContainer.add(maxLvlBg);

      const maxText = this.scene.add.text(this.detailW / 2, y + 16, 'MAX LEVEL REACHED', {
        fontSize: FontSize.sm, color: '#ffd700', fontFamily: Fonts.heading,
      });
      maxText.setOrigin(0.5);
      this.detailContainer.add(maxText);
      y += 40;
    } else {
      const levelCost = getLevelUpCost(crew.level, crew.totalXp);
      this.detailContainer.add(this.createActionBtn(0, y, this.detailW, 32, `Level Up \u2014 $${levelCost.toLocaleString()}`, 0x223366, () => this.handleLevelUp(crew)));
      y += 40;
    }

    // Dismiss (only for barracks — assigned crew must unassign first)
    if (crew.status === 'barracks') {
      this.detailContainer.add(this.createActionBtn(0, y, this.detailW, 32, 'Dismiss', 0x442222, () => {
        this.detailView = 'dismiss-confirm';
        this.renderDetail();
      }));
    }
  }

  // ============ Ship Picker Sub-view ============

  private renderShipPicker(): void {
    const crew = this.selectedCrew!;
    let y = 0;

    // Mobile back button
    if (this.mobile) {
      const backBtn = this.createMobileBackBtn();
      this.detailContainer.add(backBtn);
      y += 30;
    }

    // Back button
    this.detailContainer.add(this.createActionBtn(0, y, 90, 28, '\u2190 Back', 0x1a2a44, () => {
      this.detailView = 'info';
      this.renderDetail();
    }));
    y += 38;

    this.detailContainer.add(this.scene.add.text(0, y, `Assign ${crew.name} to:`, {
      fontSize: FontSize.md, color: CSSColors.textWhite, fontFamily: Fonts.heading,
    }));
    y += 26;

    // Get available ships (not already crewed)
    const playerUnits = this.mmoState.getPlayerUnits();
    const assignedShipIds = new Set(this.crews.filter(c => c.assignedShipId).map(c => c.assignedShipId));
    const availableShips = playerUnits.filter(u => !assignedShipIds.has(u.id));

    if (availableShips.length === 0) {
      this.detailContainer.add(this.scene.add.text(0, y, 'No available ships.\nAll ships already have crew.', {
        fontSize: FontSize.sm, color: '#556677', fontFamily: Fonts.body, lineSpacing: 4,
      }));
      y += 42;

      // Buy a ship CTA
      this.detailContainer.add(this.createActionBtn(0, y, this.detailW, 34, 'Buy a Ship', 0x224488, () => {
        this.close();
        new ShipStoreDialog(this.scene);
      }));
      return;
    }

    for (const ship of availableShips) {
      const card = this.scene.add.container(0, y);
      const cardH = 36;

      const cardBg = this.scene.add.graphics();
      cardBg.fillStyle(Colors.bgCard, 0.8);
      cardBg.fillRoundedRect(0, 0, this.detailW, cardH, Radius.sm);
      cardBg.lineStyle(1, Colors.borderDim, 0.1);
      cardBg.strokeRoundedRect(0, 0, this.detailW, cardH, Radius.sm);
      card.add(cardBg);

      card.add(this.scene.add.text(12, 9, `${ship.type}`, {
        fontSize: FontSize.sm, color: CSSColors.textWhite, fontFamily: Fonts.heading,
      }));

      card.add(this.scene.add.text(this.detailW - 12, 9, `HP ${ship.currentHp}/${ship.maxHp}`, {
        fontSize: FontSize.xs, color: '#889999', fontFamily: Fonts.body,
      }).setOrigin(1, 0));

      // Hitzone
      const hitZone = this.scene.add.zone(0, 0, this.detailW, cardH).setOrigin(0, 0);
      hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.detailW, cardH), Phaser.Geom.Rectangle.Contains);
      hitZone.on('pointerover', () => {
        cardBg.clear();
        cardBg.fillStyle(Colors.bgCardHover, 0.9);
        cardBg.fillRoundedRect(0, 0, this.detailW, cardH, Radius.sm);
        cardBg.lineStyle(1, Colors.cyan, 0.2);
        cardBg.strokeRoundedRect(0, 0, this.detailW, cardH, Radius.sm);
        this.scene.input.setDefaultCursor('pointer');
      });
      hitZone.on('pointerout', () => {
        cardBg.clear();
        cardBg.fillStyle(Colors.bgCard, 0.8);
        cardBg.fillRoundedRect(0, 0, this.detailW, cardH, Radius.sm);
        cardBg.lineStyle(1, Colors.borderDim, 0.1);
        cardBg.strokeRoundedRect(0, 0, this.detailW, cardH, Radius.sm);
        this.scene.input.setDefaultCursor('default');
      });
      hitZone.on('pointerdown', () => this.handleAssign(crew, ship.id, ship.type));
      card.add(hitZone);

      this.detailContainer.add(card);
      y += 40;
    }
  }

  // ============ Dismiss Confirmation Sub-view ============

  private renderDismissConfirm(): void {
    const crew = this.selectedCrew!;
    let y = 0;

    // Mobile back button
    if (this.mobile) {
      const backBtn = this.createMobileBackBtn();
      this.detailContainer.add(backBtn);
      y += 30;
    }

    // Back button
    this.detailContainer.add(this.createActionBtn(0, y, 90, 28, '\u2190 Back', 0x1a2a44, () => {
      this.detailView = 'info';
      this.renderDetail();
    }));
    y += 40;

    // Warning
    const warningBg = this.scene.add.graphics();
    warningBg.fillStyle(0x441111, 0.5);
    warningBg.fillRoundedRect(0, y, this.detailW, 44, Radius.sm);
    warningBg.lineStyle(1, 0xff4444, 0.25);
    warningBg.strokeRoundedRect(0, y, this.detailW, 44, Radius.sm);
    this.detailContainer.add(warningBg);

    const warningText = this.scene.add.text(this.detailW / 2, y + 13, 'DISMISS CREW?', {
      fontSize: FontSize.md, color: CSSColors.redBright, fontFamily: Fonts.heading,
    });
    warningText.setOrigin(0.5, 0);
    this.detailContainer.add(warningText);
    y += 56;

    this.detailContainer.add(this.scene.add.text(0, y, `${crew.name} (Lv.${crew.level}) will be permanently\nremoved. This cannot be undone.`, {
      fontSize: FontSize.sm, color: '#ff8888', fontFamily: Fonts.body, lineSpacing: 4,
    }));
    y += 44;

    // Confirm / Cancel
    this.detailContainer.add(this.createActionBtn(0, y, this.detailW, 34, `Dismiss ${crew.name}`, 0x662222, () => this.handleDismiss(crew)));
    y += 42;

    this.detailContainer.add(this.createActionBtn(0, y, this.detailW, 34, 'Cancel', 0x1a2a44, () => {
      this.detailView = 'info';
      this.renderDetail();
    }));
  }

  // ============ Helpers ============

  private addDivider(y: number): void {
    const g = this.scene.add.graphics();
    g.lineStyle(1, Colors.cyan, 0.08);
    g.lineBetween(0, y, this.detailW, y);
    this.detailContainer.add(g);
  }

  private addInfoRow(x: number, y: number, label: string, value: string, valueColor: string): void {
    const lbl = this.scene.add.text(x, y, `${label}: `, {
      fontSize: FontSize.xs, color: '#889999', fontFamily: Fonts.body,
    });
    this.detailContainer.add(lbl);

    const val = this.scene.add.text(x + lbl.width, y, value, {
      fontSize: FontSize.xs, color: valueColor, fontFamily: Fonts.body,
    });
    this.detailContainer.add(val);
  }

  private createSmallBtn(
    x: number, y: number, label: string, w: number, color: number, onClick: () => void,
  ): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(x, y);
    const h = 24;

    const bg = this.scene.add.graphics();
    bg.fillStyle(color, 0.8);
    bg.fillRoundedRect(0, 0, w, h, Radius.sm);
    bg.lineStyle(1, Colors.cyan, 0.15);
    bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
    btn.add(bg);

    const text = this.scene.add.text(w / 2, h / 2, label, {
      fontSize: FontSize.xs, color: '#ccddee', fontFamily: Fonts.heading,
    });
    text.setOrigin(0.5);
    btn.add(text);

    const hitZone = this.scene.add.zone(0, 0, w, h).setOrigin(0, 0);
    hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    hitZone.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(color + 0x222222, 1);
      bg.fillRoundedRect(0, 0, w, h, Radius.sm);
      bg.lineStyle(1, Colors.cyan, 0.25);
      bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
      this.scene.input.setDefaultCursor('pointer');
    });
    hitZone.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(color, 0.8);
      bg.fillRoundedRect(0, 0, w, h, Radius.sm);
      bg.lineStyle(1, Colors.cyan, 0.15);
      bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
      this.scene.input.setDefaultCursor('default');
    });
    hitZone.on('pointerdown', onClick);
    btn.add(hitZone);

    return btn;
  }

  private createActionBtn(
    x: number, y: number, w: number, h: number,
    label: string, color: number, callback: () => void,
  ): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(x, y);

    const bg = this.scene.add.graphics();
    bg.fillStyle(color, 0.8);
    bg.fillRoundedRect(0, 0, w, h, Radius.sm);
    bg.lineStyle(1, Colors.cyan, 0.2);
    bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
    btn.add(bg);

    const text = this.scene.add.text(w / 2, h / 2, label, {
      fontSize: FontSize.sm, color: CSSColors.textWhite, fontFamily: Fonts.heading,
    });
    text.setOrigin(0.5);
    btn.add(text);

    const hitZone = this.scene.add.zone(0, 0, w, h).setOrigin(0, 0);
    hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    hitZone.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(color + 0x222222, 1);
      bg.fillRoundedRect(0, 0, w, h, Radius.sm);
      bg.lineStyle(1, Colors.cyan, 0.3);
      bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
      this.scene.input.setDefaultCursor('pointer');
    });
    hitZone.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(color, 0.8);
      bg.fillRoundedRect(0, 0, w, h, Radius.sm);
      bg.lineStyle(1, Colors.cyan, 0.2);
      bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
      this.scene.input.setDefaultCursor('default');
    });
    hitZone.on('pointerdown', callback);
    btn.add(hitZone);

    return btn;
  }

  // ============ Actions ============

  private async handleRecruit(): Promise<void> {
    const names = ['Zara', 'Kael', 'Mira', 'Drex', 'Lyra', 'Voss', 'Nyx', 'Reyn', 'Sable', 'Orion',
      'Nova', 'Thane', 'Ember', 'Cade', 'Jinx', 'Rook', 'Ash', 'Vale', 'Storm', 'Flint'];
    const name = names[Math.floor(Math.random() * names.length)] + '-' + Math.floor(Math.random() * 999);

    this.setStatus(`Recruiting ${name}...`);
    const result = await this.monetization.recruitCrew(name);

    if (result.success) {
      this.setStatus(`${name} recruited!`);
      await this.loadCrews();
    } else {
      this.setStatus(`Failed: ${result.error}`);
    }
  }

  private async handleBuySlot(): Promise<void> {
    this.setStatus('Purchasing crew slot ($10)...');

    // Use PaymentFlowManager if available, otherwise fall back to monetization manager
    const paymentFlow = ServiceLocator.tryGet<PaymentFlowManager>(Services.PAYMENT_FLOW);
    let result: { success: boolean; slotNumber?: number; error?: string };

    if (paymentFlow) {
      const payResult = await paymentFlow.executePayment({
        assetType: 'crew_slot',
        amountUsd: 10,
        chain: 'solana',
        stablecoin: 'USDC',
      });
      result = { success: payResult.success, error: payResult.error };
    } else {
      result = await this.monetization.purchaseCrewSlot('solana', 'USDC');
    }

    if (result.success) {
      this.setStatus(result.slotNumber ? `Slot #${result.slotNumber} purchased!` : 'Crew slot purchased!');
      await this.loadCrews();
    } else {
      this.setStatus(`Failed: ${result.error}`);
    }
  }

  private async handleAssign(crew: Crew, shipId: string, shipType: string): Promise<void> {
    this.setStatus(`Assigning ${crew.name} to ${shipType}...`);
    const result = await this.monetization.assignCrew(crew.id, shipId);

    if (result.success) {
      this.setStatus(`${crew.name} assigned to ${shipType}!`);
      this.detailView = 'info';
      await this.loadCrews();
    } else {
      this.setStatus(`Failed: ${result.error}`);
    }
  }

  private async handleUnassign(crew: Crew): Promise<void> {
    this.setStatus(`Unassigning ${crew.name}...`);
    const result = await this.monetization.unassignCrew(crew.id);

    if (result.success) {
      this.setStatus(`${crew.name} returned to barracks`);
      await this.loadCrews();
    } else {
      this.setStatus(`Failed: ${result.error}`);
    }
  }

  private async handleLevelUp(crew: Crew): Promise<void> {
    const cost = getLevelUpCost(crew.level, crew.totalXp);
    this.setStatus(`Training ${crew.name} ($${cost})...`);
    const result = await this.monetization.levelUpCrew(crew.id);

    if (result.success) {
      this.setStatus(`${crew.name} leveled up to ${result.newLevel}!`);
      await this.loadCrews();
    } else {
      this.setStatus(`Failed: ${result.error}`);
    }
  }

  private async handleDismiss(crew: Crew): Promise<void> {
    this.setStatus(`Dismissing ${crew.name}...`);
    const result = await this.monetization.dismissCrew(crew.id);

    if (result.success) {
      this.setStatus(`${crew.name} dismissed`);
      this.selectedCrew = null;
      this.detailView = 'info';
      await this.loadCrews();
    } else {
      this.setStatus(`Failed: ${result.error}`);
    }
  }

  private setStatus(message: string): void {
    this.statusText.setText(message);
  }
}
